import apiClient from 'api/services/apiClient';
import { MESSAGE_COMMON } from 'constants/string/messages';
import { isApiResponseError, validateMessageResponse } from 'utils/helper';

const normalizeListResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeObjectResponse = (payload) => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload?.data) && payload?.data) {
    return payload.data;
  }
  return payload;
};

/**
 * @name videoSamples
 * @description Create a new video sample
 * @param {object} data
 * @response success - 200 OK
 * @response failure - 400 Bad Request
 * @returns {object} response.data
 */
export const videoSamples = async (data) => {
  const response = await apiClient.request('POST', '/admin/file-samples', data, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!isApiResponseError(response)) {
    const responseData = normalizeObjectResponse(response?.data);
    return {
      ...responseData,
      ...validateMessageResponse(response?.data?.message || response?.data?.jp_message, 'string', MESSAGE_COMMON.SUCCESS),
    };
  } else {
    throw {
      ...validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE),
      field_names: response?.error?.field_names || [],
    };
  }
};

/**
 * @name videoSamplesList
 * @description Get the list of video samples
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const videoSamplesList = async (query) => {
  const response = await apiClient.request('GET', `/admin/file-samples${query}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name videoSamplesDeleteByIds
 * @description Delete a video sample by ids
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const videoSamplesDeleteByIds = async (query) => {
  const response = await apiClient.request('DELETE', `/admin/file-samples${query}`);
  if (!isApiResponseError(response)) {
    return true;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name videoSamplesUpdate
 * @description Update a video sample by category id
 * @param {object} data
 * @param {string} categoryId
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const videoSamplesUpdate = async (data, categoryId) => {
  const response = await apiClient.request('PUT', `/admin/file-samples/${categoryId}`, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!isApiResponseError(response)) {
    const responseData = normalizeObjectResponse(response?.data);
    return {
      ...responseData,
      ...validateMessageResponse(response?.data?.message || response?.data?.jp_message, 'string', MESSAGE_COMMON.SUCCESS),
    };
  } else {
    throw {
      ...validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE),
      field_names: response?.error?.field_names || [],
    };
  }
};

/**
 * @name videoSamplesGetByCategoryIds
 * @description Get video samples by category ids
 * @param {string} queryString
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const videoSamplesGetByCategoryIds = async (queryString) => {
  const response = await apiClient.request('GET', `/admin/file-samples/get-by-ids${queryString}`);
  if (!isApiResponseError(response)) {
    return normalizeListResponse(response?.data);
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};
